import { AFFINITY, type AffinityType } from './damage.ts';
import type { UnsleepingId } from './unsleeping.ts';
import { sec } from './units.ts';
/** Experience (SPEC §14). */
export const XP = {
  incursion: 100,
  liveWave: 250,
  ghostWave: 80,
  task: 60,
  boss: 1000,
  campaignWon: 3000,
};

/** XP needed to go from level n to n+1: 400 × n^1.5, rounded to tens. Precomputed (n = 1..49, up to MAX_LEVEL). */
export const XP_TO_NEXT: number[] = [0, 400, 1130, 2080, 3200, 4470, 5880, 7410, 9050, 10800, 12650, 14590, 16630, 18750, 20950, 23240, 25600, 28040, 30550, 33130, 35780, 38490, 41280, 44120, 47030, 50000, 53030, 56120, 59260, 62470, 65730, 69040, 72410, 75830, 79300, 82830, 86400, 90020, 93700, 97420, 101190, 105010, 108880, 112790, 116750, 120750, 124790, 128890, 133020, 137200];

/** One skill point per level from level 2. */
export const skillPointsAtLevel = (level: number): number => Math.max(0, level - 1);

/** `unique`: the saga's pieces (docs/QUESTS.md), one per class and slot, never rolled. */
/** `veryRare` (docs/EQUIPO.md §3): a third attribute on a rare, only on pieces of power POWER.veryRareFrom and up. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'veryRare' | 'unique';
export const RARITY_ATTRS: Record<Rarity, number> = { common: 1, uncommon: 2, rare: 2, veryRare: 3, unique: 2 };
/** The kinds of piece (docs/EQUIPO.md §1); a hero wears two rings (WornSlot in gear.ts). */
export type GearSlot = 'weapon' | 'offhand' | 'head' | 'armor' | 'boots' | 'amulet' | 'ring';
/** The three slots from before bases: what an old-style roll draws from, and where the saga's unique pieces go. */
export type UniqueSlot = 'weapon' | 'armor' | 'amulet';
export const GEAR_SLOTS: UniqueSlot[] = ['weapon', 'armor', 'amulet'];
/** `crit` (certeza): a share of the roll turns into critical chance (CRIT.attrBp); only weapons and amulets carry it. */
/** `res…` (afinidad, docs/CRIATURAS.md §3): guards against one damage type; armor and amulets only, one per piece, rolled as one choice. */
export type ResAttr = 'resFire' | 'resFrost' | 'resLightning' | 'resAcid' | 'resPoison' | 'resShadow';
export type GearAttr = 'damage' | 'hp' | 'attackSpeed' | 'cooldown' | 'armor' | 'crit' | ResAttr;
export const RES_ATTRS: ResAttr[] = ['resFire', 'resFrost', 'resLightning', 'resAcid', 'resPoison', 'resShadow'];
export const GEAR_ATTRS: GearAttr[] = ['damage', 'hp', 'attackSpeed', 'cooldown', 'armor', 'crit', ...RES_ATTRS];
export const RES_TYPE: Record<ResAttr, AffinityType> = { resFire: 'fire', resFrost: 'frost', resLightning: 'lightning', resAcid: 'acid', resPoison: 'poison', resShadow: 'shadow' };
export const isResAttr = (k: GearAttr): k is ResAttr => k in RES_TYPE;
/** The slots an attribute can come on (absent: every slot). */
export const GEAR_ATTR_SLOTS: Partial<Record<GearAttr, GearSlot[]>> = {
  crit: ['weapon', 'amulet'],
  ...Object.fromEntries(RES_ATTRS.map((k) => [k, ['armor', 'amulet']])),
};
export const attrOnSlot = (k: GearAttr, slot: GearSlot): boolean => GEAR_ATTR_SLOTS[k]?.includes(slot) ?? true;

/**
 * Critical hits (docs/CRIATURAS.md §6): heroes only. Each class's base chance and how hard a crit lands
 * (bp); certeza adds `attrBp` of its value as chance, up to `maxBp`. A nightmare that can't fight back
 * (stunned, held by roots or a net) always takes a critical.
 */
export const CRIT = {
  chanceBp: { warrior: 300, artificer: 500, mage: 500, ranger: 800, cleric: 400, barbarian: 800, paladin: 300, druid: 500 } as Record<string, number>,
  multBp: { warrior: 18000, artificer: 15000, mage: 15000, ranger: 15000, cleric: 15000, barbarian: 16000, paladin: 18000, druid: 15000 } as Record<string, number>,
  attrBp: 5000,
  maxBp: 4000,
};
/** What an attribute's value is worth as shown and as applied (certeza counts at CRIT.attrBp). */
export const attrValue = (k: GearAttr, v: number): number =>
  k === 'crit' ? Math.trunc((v * CRIT.attrBp) / 10000) : isResAttr(k) ? Math.trunc((v * AFFINITY.attrBp) / 10000) : v;
/** Attribute roll ranges in bp by rarity. */
export const GEAR_ROLL_BP: Record<Rarity, [number, number]> = {
  common: [400, 800],
  uncommon: [600, 1100],
  rare: [900, 1500],
  veryRare: [900, 1500],
  unique: [1500, 1800],
};
export type GearEffect = 'burn' | 'lifesteal' | 'thorns' | 'swift' | 'fury' | 'execute' | 'chain' | 'frost' | 'cleave' | 'guard' | 'echo' | 'banner';
export const GEAR_EFFECTS: GearEffect[] = ['burn', 'lifesteal', 'thorns', 'swift', 'fury', 'execute', 'chain', 'frost', 'cleave', 'guard', 'echo', 'banner'];
/**
 * What a rare piece's effect does (bp). The same effect on more than one piece adds up by GEAR_STACK_BP.
 * - burn: the basic attack sets the target alight for `bp` of the blow over `ticks`, one pulse every `every` (renewed, not stacked);
 * - lifesteal: the basic attack heals `bp` of the damage it deals;
 * - thorns: a nightmare striking the hero in melee takes `attackBp` of the hero's attack back, through armor
 *   (of the attack, not the blow: nightmares hit for little, so a share of the blow was next to nothing);
 * - swift: `bp` more movement speed;
 * - fury: below `belowBp` of its health, the hero's basic attack hits `bp` harder;
 * - execute: the basic attack hits `bp` harder a nightmare below `belowBp` of its health;
 * - chain: every `every`-th basic attack that lands arcs to up to `targets` nightmares within `range`, for `bp` of the blow;
 * - frost: the basic attack slows the target by `bp` for `ticks` (half on elites and bosses);
 * - cleave: the basic attack also hits the nightmares within `radius` of its target, for `bp` of the blow;
 * - guard: every `every` ticks a shield of `bp` of the hero's health rises for `ticks`;
 * - echo: each nightmare the hero fells takes `ticks` off its skills' recharge;
 * - banner: towers within `range` of the standing hero hit `bp` harder (the best banner nearby counts, they don't add).
 */
export const GEAR_EFFECT_FX = {
  burn: { bp: 2500, ticks: 36, every: 12 },
  lifesteal: { bp: 800 },
  thorns: { attackBp: 2500 },
  swift: { bp: 1200 },
  fury: { bp: 3000, belowBp: 3500 },
  execute: { bp: 4000, belowBp: 3000 },
  chain: { bp: 5000, every: 4, targets: 2, range: 2200 },
  frost: { bp: 2500, ticks: 18 },
  cleave: { bp: 3000, radius: 1000 },
  guard: { bp: 800, every: 12 * 12, ticks: 6 * 12 },
  echo: { ticks: 6 },
  banner: { bp: 1000, range: 3000 },
};
/**
 * The same effect on several pieces: the first counts whole, the second for 60 % and the third for 30 %
 * (three pieces with thorns give 190 % of one, not 300 %). Mixing effects pays more than stacking one.
 */
export const GEAR_STACK_BP = [10000, 6000, 3000];
/** Each plus adds `perPlusBp` points to the item's main attribute; the forge's level caps the plus (level 1 → +3, 2 → +4, 3 → +5, 4 → +6 from era III, 5 → +7 from era IV, 6 → +8 from era V: FORGE_LEVEL_ERA). */
export const FORGE = { maxPlus: 8, perPlusBp: 400, dustPerLevel: 25, maxPlusByLevel: [3, 4, 5, 6, 7, 8] };
/** Melting a piece in the forge: dream dust into the common storage, plus half of what its pluses cost. */
export const SALVAGE_DUST: Record<Rarity, number> = { common: 10, uncommon: 25, rare: 60, veryRare: 120, unique: 0 };
export const SALVAGE_PLUS_BP = 5000;
export const DROP = { incursionBp: 2500, eliteBp: 10000 };
/** Chance of rarity on a drop, in bp (elites and incursions). Bosses guarantee rare. */
export const RARITY_WEIGHTS: Record<Rarity, number> = { common: 6000, uncommon: 3200, rare: 800, veryRare: 0, unique: 0 };

/**
 * Reforging at the forge: one attribute of a piece becomes another one it doesn't have, rolled anew in
 * its rarity's range (the pluses forged into it move with it). Dust by rarity, `stepBp` more each time
 * the same piece is reforged.
 */
/** Carving an affinity at the forge: the same value turned to another damage type, for dust (cheaper than a reforge: it is meant for answering an omen). */
export const ATTUNE = { dust: { common: 25, uncommon: 50, rare: 100, veryRare: 150, unique: 0 } as Record<Rarity, number> };
/**
 * A unique piece is reforged too, but its hero picks what the attribute becomes and the value stays
 * (the piece is one of a kind and its fixed attributes don't suit every role: a tank wants life, not speed).
 */
export const REFORGE = { dust: { common: 40, uncommon: 90, rare: 180, veryRare: 270, unique: 150 } as Record<Rarity, number>, stepBp: 5000 };

/**
 * Obsidian settings: shards open one setting in a rare piece, and crystal from the storage sets a gem in
 * it (changing the gem pays the gem again; the old one breaks). Each gem adds `bp` to one attribute, on top of the
 * piece's own (the gear caps still hold).
 */
export type GemId = 'ruby' | 'garnet' | 'sapphire' | 'amethyst' | 'onyx' | 'emerald';
export const GEMS: GemId[] = ['ruby', 'garnet', 'sapphire', 'amethyst', 'onyx', 'emerald'];
/** What each gem adds to (the emerald, certeza: a share of it as critical chance, like the attribute). */
export const GEM_ATTR: Record<GemId, GearAttr> = { ruby: 'damage', garnet: 'hp', sapphire: 'attackSpeed', amethyst: 'cooldown', onyx: 'armor', emerald: 'crit' };
export const SOCKET = { shards: 10, dust: 150, gemCrystal: 80, gemDust: 60, bp: 800 };

export type UnsleepingRelicId = 'corvinGate' | 'velaString' | 'anselPage' | 'lucanCenser' | 'bruniHammer' | 'ragnaHorn' | 'eddaShield' | 'kaelaSeed';
export type RelicId = 'fateDie' | 'vigilGrimoire' | 'vigilLantern' | 'dreamerRing' | 'warHorn' | 'ashAmulet' | UnsleepingRelicId;
/** The relics that drop (bosses, chests, nodes, spare chances): the random pools draw only from these. */
export const RELICS: RelicId[] = ['fateDie', 'vigilGrimoire', 'vigilLantern', 'dreamerRing', 'warHorn', 'ashAmulet'];
/**
 * The Unsleeping's relics (docs/ANTIHEROES.md, «Su reliquia»): one each, only from his felling for good, to the hero
 * who struck the last blow. Never in a random pool.
 */
export const UNSLEEPING_RELICS: UnsleepingRelicId[] = ['corvinGate', 'velaString', 'anselPage', 'lucanCenser', 'bruniHammer', 'ragnaHorn', 'eddaShield', 'kaelaSeed'];
export const UNSLEEPING_RELIC: Record<UnsleepingId, UnsleepingRelicId> = {
  corvin: 'corvinGate',
  vela: 'velaString',
  ansel: 'anselPage',
  lucan: 'lucanCenser',
  bruni: 'bruniHammer',
  ragna: 'ragnaHorn',
  edda: 'eddaShield',
  kaela: 'kaelaSeed',
};
/** Every relic a hero may hold (what commands and the protocol accept). */
export const ALL_RELICS: RelicId[] = [...RELICS, ...UNSLEEPING_RELICS];
export const MAX_RELICS = 2;
export const RELIC_FX = {
  /** Every basic attack rolls a die of `faces`: on the top `lucky` faces the blow hits twice as hard. */
  fateDie: { faces: 6, lucky: 1 },
  vigilGrimoire: {},
  vigilLantern: { revealRange: 3500 },
  dreamerRing: {},
  warHorn: { range: 3500, fireRateBp: 1500 },
  ashAmulet: { healPerKill: 8 },
  /** Blows from a nightmare in front (within `frontHalf` of the hero's facing, 1024 a turn) land at `takenBp`. */
  corvinGate: { takenBp: 7500, frontHalf: 170 },
  /** What the hero strikes is smoked `ticks`: it takes `markBp` of every blow (the stronger mark wins). */
  velaString: { markBp: 11200, ticks: sec(4) },
  /** Every `every`-th basic attack bursts in fire around its target: `damageBp` of the blow within `radius`. */
  anselPage: { every: 6, radius: 1500, damageBp: 8000 },
  /** A nightmare dying within `range` of the hero heals every standing hero within `range` of it `healBp` of their own max health. */
  lucanCenser: { range: 3000, healBp: 100 },
  /** Structures within `range` of the standing hero take `takenBp` of every blow (the best hammer counts, they don't add). */
  bruniHammer: { range: 3000, takenBp: 7000 },
  /** The hero's own damage: `dmgBp` more for every `perBp` of its health lost. */
  ragnaHorn: { perBp: 1000, dmgBp: 300 },
  /** Once a fight, a blow that would fell the hero leaves it untouchable `invuln` ticks, healed to `healBp` of its max health. */
  eddaShield: { invuln: sec(2), healBp: 2000 },
  /** A nightmare that stays `after` ticks within `range` of the hero is pinned `stun` ticks (once each; not bosses, elites or guardians). */
  kaelaSeed: { range: 2500, after: sec(2), stun: sec(1) },
} as const;
/**
 * An awakened relic (at the forge, for shards, embers and dust): the same relic, stronger.
 * - fateDie: a 5 or a 6 doubles the blow, not just a 6;
 * - vigilGrimoire: the spectre's ultimate twice a wave, and the live hero's ultimate recharges `ultCdBp` faster;
 * - vigilLantern: a wider light, and the stalkers it reveals take `revealedTakenBp` more;
 * - dreamerRing: back in `respawnTicks` instead of 10 s;
 * - warHorn: farther and faster;
 * - ashAmulet: twice the healing per nightmare felled;
 * - the Unsleeping's relics: the design table's awakened value (docs/ANTIHEROES.md).
 */
export const RELIC_AWAKENED_FX = {
  fateDie: { lucky: 2 },
  vigilGrimoire: { uses: 2, ultCdBp: 2000 },
  vigilLantern: { revealRange: 5000, revealedTakenBp: 2000 },
  dreamerRing: { respawnTicks: 5 * 12 },
  warHorn: { range: 5000, fireRateBp: 2500 },
  ashAmulet: { healPerKill: 16 },
  corvinGate: { takenBp: 6500 },
  velaString: { markBp: 12000 },
  anselPage: { every: 4 },
  lucanCenser: { healBp: 200 },
  bruniHammer: { takenBp: 5500 },
  ragnaHorn: { dmgBp: 500 },
  eddaShield: { invuln: sec(3), healBp: 3500 },
  kaelaSeed: { stun: sec(1.5) },
} as const;
export const RELIC_AWAKEN = { shards: 12, embers: 20, dust: 600 };
/** A relic chance that lands when the hero already has every relic: the relic melts into embers. */
export const RELIC_SPARE_EMBERS = 6;

/**
 * Keeping pace with the group: a hero more than `belowBy` levels under the average of the rest of the
 * group (those still in it) earns `xpBp` of the XP, until it catches up.
 */
export const CATCH_UP = { belowBy: 2, xpBp: 20000 };
/**
 * Relic chances from the bosses the group meets before the wave bosses (bp, per hero who fought): the
 * crypt's Warden on every won run (the chest has its own chance), the crypt in embers' Warden, the magma
 * lord of a level II incursion, and a warlord's den.
 */
export const RELIC_DROPS = { warden: 1500, wardenEmbers: 2500, magmaLord: 2000, colossus: 2500, lair: 3000 };
/** Boss relic drops. */
export const BOSS_RELICS: Record<'witch' | 'shell', RelicId[]> = {
  witch: ['vigilLantern', 'vigilGrimoire', 'warHorn'],
  shell: ['fateDie', 'dreamerRing', 'ashAmulet'],
};

/** What gear can add up to, at most (bp): past this, more of it on gear does nothing (the skill tree adds on top). */
export const GEAR_CAPS = { cooldownBp: 4000, armorBp: 3000 };
